import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { matchPageName } from "hooks/usePageViewTracker";
import { SEARCH_TRIGGER, SEARCH_TYPE, SEARCH_URL_PARAM, trackSearchRun } from "hooks/semanticSearchAnalytics";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 500;

// Its own URL param, NOT ?q=. LessonsStandards calls useSemanticStandardSearch
// unconditionally — in lesson mode too — and that hook re-runs its search from
// whatever ?q= it sees on mount. Sharing the param makes the two fight: typing
// here would fire a wasted standard search whose stale results then surface on
// the next mode toggle.
const URL_PARAM = SEARCH_URL_PARAM[SEARCH_TYPE.LESSON];

// Debounced, abortable wrapper around POST /curriculum/lessons/semantic-search
// with ?lq= URL sync (so refresh / back-nav keep the search active). Mirrors
// useSemanticStandardSearch; the curriculum is resolved server-side from the
// educator's active school, so this takes no arguments.
export const useSemanticLessonSearch = () => {
  const history = useHistory();
  const location = useLocation();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const abortRef = useRef(null);
  const lastQueryRef = useRef("");
  const restoringFromUrlRef = useRef(false);

  const writeUrlQuery = useCallback((next) => {
    if (restoringFromUrlRef.current) return;
    const params = new URLSearchParams(location.search);
    if (next) {
      params.set(URL_PARAM, next);
      history.push(`${location.pathname}?${params.toString()}`);
    } else if (params.has(URL_PARAM)) {
      params.delete(URL_PARAM);
      const rest = params.toString();
      history.replace(`${location.pathname}${rest ? `?${rest}` : ""}`);
    }
  }, [history, location.pathname, location.search]);

  const performSearch = useCallback(async (raw, { skipUrlUpdate = false, trigger } = {}) => {
    const trimmed = raw.trim();
    if (trimmed === lastQueryRef.current && !skipUrlUpdate) return;

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setShowResults(false);
      lastQueryRef.current = "";
      if (!skipUrlUpdate) writeUrlQuery("");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const controller = abortRef.current;

    setIsSearching(true);
    lastQueryRef.current = trimmed;
    try {
      const response = await request("curriculum/lessons/semantic-search", {
        method: "POST",
        payload: { query: trimmed, limit: 50, similarity_threshold: 0.2 },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setResults(response.results);
      setShowResults(true);
      if (!skipUrlUpdate) {
        trackSearchRun({
          pageName: matchPageName(location.pathname),
          searchType: SEARCH_TYPE.LESSON,
          query: trimmed,
          resultCount: response.results.length,
          trigger,
        });
        writeUrlQuery(trimmed);
      }
    } catch (e) {
      if (e.name === "AbortError" || controller.signal.aborted) return;
      logError("Failed to perform lesson search", e, { category: LogCategory.ERROR });
      setResults([]);
      setShowResults(true);
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }, [writeUrlQuery, location.pathname]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (value.trim().length < MIN_QUERY_LENGTH) {
      setResults(null);
      setShowResults(false);
      setIsSearching(false);
      lastQueryRef.current = "";
      writeUrlQuery("");
      return;
    }

    timeoutRef.current = setTimeout(() => {
      performSearch(value, { trigger: SEARCH_TRIGGER.TYPING });
    }, DEBOUNCE_MS);
  }, [performSearch, writeUrlQuery]);

  const handleSubmit = useCallback(() => {
    const value = inputRef.current?.value ?? query;
    if (value.trim().length < MIN_QUERY_LENGTH) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    performSearch(value, { trigger: SEARCH_TRIGGER.ENTER });
  }, [performSearch, query]);

  const clearSearch = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setShowResults(false);
    setResults(null);
    setQuery("");
    setIsSearching(false);
    lastQueryRef.current = "";
    writeUrlQuery("");
  }, [writeUrlQuery]);

  // Sync with lq in URL on mount / when URL changes (refresh, back/forward, or
  // sibling navigation that strips ?lq= — e.g. picking a lesson from the tree).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get(URL_PARAM);
    restoringFromUrlRef.current = true;
    if (q) {
      if (q !== query) setQuery(q);
      if (q.trim().length >= MIN_QUERY_LENGTH && q.trim() !== lastQueryRef.current) {
        performSearch(q, { skipUrlUpdate: true });
      }
    } else {
      if (query !== "") setQuery("");
      setShowResults(false);
      setResults(null);
      lastQueryRef.current = "";
    }
    const id = setTimeout(() => { restoringFromUrlRef.current = false; }, 100);
    return () => clearTimeout(id);
  // query intentionally omitted — we only want to react to URL changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return {
    query,
    results,
    isSearching,
    showResults,
    handleInputChange,
    handleSubmit,
    clearSearch,
    inputRef,
  };
};
